import { InputHTMLAttributes, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { PaperClipIcon, TrashIcon } from '@heroicons/react/24/outline';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { BaseInputProps } from '@/components/ui/custom/dataInput/BaseInput';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import FileInputZone from '@/components/ui/custom/input/FileInputZone';

type ResourceFileUploadProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'className' | 'onChange'
> &
  BaseInputProps & {
    value?: string | File | null;
    onChange?(file: File | null): void;
    onRemove?(): void;
    limit?: number;
  };

export default function ResourceFileUpload({
  value,
  className,
  accept = '.png,.jpg,.jpeg,.pdf,.zip',
  testId,
  label,
  limit = Infinity,
  labelPosition = 'top',
  errorLabelText,
  onChange,
  onRemove,
  ...rest
}: ResourceFileUploadProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [file, setFile] = useState<File | null>(
    value instanceof File ? value : null,
  );

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;

    const newFile = files[0];

    setFile(newFile);
    onChange?.(newFile);
  };

  const handleRemoveFile = () => {
    setFile(null);
    onRemove?.();
  };

  const fileName = file?.name || '';

  return (
    <section
      className={twMerge(
        'flex w-full rounded-lg relative gap-1',
        labelPosition === 'left' ? '' : 'flex-col',
        className?.base,
      )}
    >
      <FileInputZone
        onChange={handleFileChange}
        accept={accept}
        limit={limit}
        label={label}
        testId={testId}
        labelPosition={labelPosition}
        errorLabelText={errorLabelText}
        className={className}
        {...rest}
      />
      <ConditionalRenderer condition={file}>
        <section className="flex flex-row items-center w-full p-2 gap-1">
          <PaperClipIcon className="w-5 h-5 text-base-content/70" />
          <Text className="truncate text-base-content/70" text={fileName} />
          <Tooltip
            text={t('remove')}
            className="lowercase h-fit"
            classNameContainer="flex items-center"
          >
            <IconButton
              className="text-primary flex-row-reverse gap-1 font-500"
              color="custom"
              onClick={handleRemoveFile}
              icon={<TrashIcon className="w-4 h-4 text-error" />}
            />
          </Tooltip>
        </section>
      </ConditionalRenderer>
    </section>
  );
}
